<script lang="ts" setup>
import { OtIcon } from '@1trading/design-system';
import { computed, onMounted, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import type { BalanceModel } from '@exchange/libs/balances/service/src';
import XButton from '@exchange/libs/components/x-components/src/lib/XButton.vue';
import XLogo from '@exchange/libs/components/x-components/src/lib/XLogo.vue';
import { marginService } from '@exchange/libs/futures/service/src';
import { CURRENCY_ELIGIBLE_FOR_MARGIN } from '@exchange/libs/futures/service/src/lib/constants.config';
import { createDivTableColumnInstance } from '@exchange/libs/order/my-orders/ui/src/lib/components/div-table-helpers';
import DivTable from '@exchange/libs/order/my-orders/ui/src/lib/components/DivTable.vue';
import DivTableCell from '@exchange/libs/order/my-orders/ui/src/lib/components/DivTableCell.vue';
import DivTableRow from '@exchange/libs/order/my-orders/ui/src/lib/components/DivTableRow.vue';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import BalancesInfoMessageBlock from './BalancesInfoMessageBlock.vue';

const { t } = useI18n({ useScope: 'global' });

defineProps({
  authenticatedUser: { type: Boolean, required: true },
  historySource: { type: Object as PropType<HistoryDataSource>, required: true },
  sortedList: { type: Array as PropType<BalanceModel[]>, required: true },
});

const emit = defineEmits<{
  (e: 'deposit', currencyCode?: string): void;
  (e: 'transfer', currencyCode: string): void;
}>();

const balanceContainerContentRef = ref<HTMLElement>();
const balanceContainerContentTop = ref<number>();

const { getCurrencyName } = currencyService;
const { balance: marginBalance, equity, marginUsedPercentage } = marginService;

onMounted(() => {
  if (!balanceContainerContentRef.value) {
    return;
  }

  const { top } = balanceContainerContentRef.value.getBoundingClientRect();

  balanceContainerContentTop.value = top;
});

const balancesHeaders = computed(() => [
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.asset'),
    columnClass: 'asset align-left',
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.totalBalance'),
    columnClass: 'available align-left',
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.availableMargin'),
    columnClass: 'in-order align-left',
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.usedMargin'),
    columnClass: 'in-order align-left',
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.usedForMargin'),
    columnClass: 'in-order align-left',
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: t('modules.myOrders.header.eligibleForCollateral'),
    columnClass: 'in-order align-left',
    sortable: false,
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
  createDivTableColumnInstance({
    title: '',
    columnClass: 'in-order align-right',
    sortable: false,
    sortFunction: () => 0,
    sorted: false,
    sortDirection: 'asc',
    hidden: false,
  }),
]);

const gridColumnsStyle = computed(() => ({ 'grid-template-columns': '1fr 1fr 1fr 1fr 1fr 1fr 1fr' }));
</script>

<template>
  <balances-info-message-block
    v-if="!sortedList.length || !authenticatedUser"
    :authenticated-user="authenticatedUser"
  />
  <div
    v-else
    class="balances-list-exchange-wrapper"
  >
    <div-table
      class="balances-list-exchange"
      :header="balancesHeaders"
      :rows="sortedList"
      :sticky-header="true"
      :style="gridColumnsStyle"
      @header-changed="() => {}"
    >
      <template #default="{}">
        <template
          v-for="balance in sortedList"
          :key="balance.currencyCode"
        >
          <div-table-row
            class="balances-item"
            :data-id="balance.currencyCode"
            :style="gridColumnsStyle"
          >
            <div-table-cell class="balances-item__cell asset align-left">
              <x-logo
                class="balances-item__cell__icon"
                :name="balance.currencyCode"
                type="crypto"
              />
              <div class="balances-item__cell__code-wrapper">
                <span class="balances-item__cell__code">{{ balance.currencyCode }}</span>
                <span class="balances-item__cell__name">{{ getCurrencyName(balance.currencyCode) }}</span>
              </div>
            </div-table-cell>
            <div-table-cell class="balances-item__cell available align-left">
              {{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? equity.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.total }}
            </div-table-cell>
            <div-table-cell class="balances-item__cell in-order align-left">
              {{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginBalance.available.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.available }}
            </div-table-cell>
            <div-table-cell class="balances-item__cell in-order align-left">
              {{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginBalance.used.toFixed(CONSTANTS.PRECISION.DEFAULT_FIAT) : balance.locked }}
            </div-table-cell>
            <div-table-cell class="balances-item__cell in-order align-left">
              {{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? marginUsedPercentage + '%' : '0%' }}
            </div-table-cell>
            <div-table-cell class="balances-item__cell in-order align-left">
              {{ balance.currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN ? 'Yes' : 'No' }}
            </div-table-cell>
            <div-table-cell class="balances-item__cell__actions in-order align-right">
              <x-button
                class="balances-item__cell__action-button"
                size="3xs"
                variant="tertiary"
                @click="() => emit('deposit', balance.currencyCode)"
              >
                <ot-icon name="add-square" />
              </x-button>
              <x-button
                class="balances-item__cell__action-button"
                size="3xs"
                variant="tertiary"
                @click="() => emit('transfer', balance.currencyCode)"
              >
                <ot-icon name="arrow-swap-horizontal" />
              </x-button>
            </div-table-cell>
          </div-table-row>
        </template>
      </template>
    </div-table>
    <div class="balances-list-exchange__add-funds">
      <x-button
        variant="primary"
        size="sm"
        @click="() => emit('deposit')"
      >
        Add funds
      </x-button>
    </div>
  </div>
</template>

<style lang="scss">
/* stylelint-disable */

.asset {
  display: flex;
  align-items: center;
}

.balances-list-exchange {
  display: grid;

  .div-table-header,
  .balances-item {
    display: contents;
  }

  .div-table-header-cell,
  .balances-item__cell {
    padding: 8px;
    border-bottom: 1px solid rgb(var(--v-theme-border));
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-regular);

    &__icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }

    &__code-wrapper {
      display: flex;
      flex-direction: column;
    }

    &__code {
      color: rgb(var(--v-theme-text-primary));
      font-size: var(--font-size-sm);
    }

    &__name {
      color: rgb(var(--v-theme-text-secondary));
      font-size: var(--font-size-xxs);
      font-weight: var(--font-weight-regular);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    &__action-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      background-color: transparent;
      color: rgb(var(--v-theme-text-primary));
      cursor: pointer;
    }
  }

  .div-table-header-cell {
    background-color: rgb(var(--v-theme-elevation-1));
    font-weight: bold;
  }

  .align-left {
    text-align: left;
  }

  .align-right {
    text-align: right;
  }

  .align-center {
    text-align: center;
  }

  &__add-funds {
    display: flex;
    justify-content: center;
    margin: 16px;
  }
}
</style>
